import { useState } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import GcpRecommendConfirmModal from '@/components/gcp/GcpRecommendConfirmModal'
import { HoverHelp } from '@/tableControls'
import { WINDOW_PRESETS } from '@/services/gcpCosts'

interface GcpRunControlsProps {
  windowPresetId: string
  metricsDays: number
  canEdit: boolean
  isRunning: boolean
  isRecommendRunning: boolean
  hasResults: boolean
  lastRunAt: string | null
  onPresetChange: (presetId: string) => void
  onMetricsDaysChange: (days: number) => void
  onRun: () => void
  onGenerateRecommendations: () => void
}

function formatLastRun(iso: string | null, presetLabel: string): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const time = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  return `Last run: ${time} · window: ${presetLabel}`
}

const TIME_WINDOW_HELP =
  'Billing trends use whole calendar months from the selected preset (30 days → 1 month, 60 → 2, 90 → 3, 6 months → 6).'

const UTILIZATION_HELP =
  'Utilization metrics and BigQuery query logs use the most recent N days. Cloud Monitoring only retains ~6 weeks of some metrics; BigQuery job logs retain 180 days.'

const FIELD_CLASS =
  'h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-60'

export default function GcpRunControls({
  windowPresetId,
  metricsDays,
  canEdit,
  isRunning,
  isRecommendRunning,
  hasResults,
  lastRunAt,
  onPresetChange,
  onMetricsDaysChange,
  onRun,
  onGenerateRecommendations,
}: GcpRunControlsProps) {
  const [showRecommendConfirm, setShowRecommendConfirm] = useState(false)
  const activePreset = WINDOW_PRESETS.find((p) => p.id === windowPresetId) ?? WINDOW_PRESETS[2]!
  const lastRunLine = formatLastRun(lastRunAt, activePreset.label)

  const closeRecommendConfirm = () => setShowRecommendConfirm(false)

  const handleConfirmRecommend = () => {
    onGenerateRecommendations()
    setShowRecommendConfirm(false)
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between lg:gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-4">
          <div className="w-full sm:w-auto sm:min-w-[11rem]">
            <div className="text-sm font-medium text-gray-700">
              <HoverHelp label="Time window" aria-label="Time window help">
                <p>{TIME_WINDOW_HELP}</p>
              </HoverHelp>
            </div>
            <select
              id="gcp-time-window"
              value={windowPresetId}
              disabled={!canEdit || isRunning}
              onChange={(e) => onPresetChange(e.target.value)}
              className={`mt-1.5 w-full sm:w-44 ${FIELD_CLASS}`}
              aria-label="Time window"
            >
              {WINDOW_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full sm:w-auto">
            <div className="text-sm font-medium text-gray-700">
              <HoverHelp label="Utilization metrics" aria-label="Utilization metrics help">
                <p>{UTILIZATION_HELP}</p>
              </HoverHelp>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                id="gcp-metrics-days"
                type="number"
                min={7}
                max={90}
                value={metricsDays}
                disabled={!canEdit || isRunning}
                onChange={(e) => {
                  const next = Number(e.target.value)
                  if (Number.isFinite(next)) {
                    onMetricsDaysChange(Math.min(90, Math.max(7, next)))
                  }
                }}
                className={`w-20 ${FIELD_CLASS}`}
                aria-label="Utilization metrics days"
              />
              <span className="text-sm text-gray-500">days</span>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-end">
          <Button
            variant="primary"
            gradient
            size="md"
            disabled={!canEdit || isRunning}
            onClick={onRun}
            className="w-full sm:w-auto"
          >
            {isRunning ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Running analysis…
              </>
            ) : hasResults ? (
              'Re-run analysis'
            ) : (
              'Run analysis'
            )}
          </Button>

          {hasResults ? (
            <Button
              variant="outline"
              size="md"
              disabled={!canEdit || isRunning || isRecommendRunning}
              onClick={() => setShowRecommendConfirm(true)}
              className="w-full sm:w-auto"
            >
              {isRecommendRunning ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                  Generating…
                </>
              ) : (
                'Get recommendations'
              )}
            </Button>
          ) : null}
        </div>
      </div>

      {lastRunLine ? <p className="mt-2 text-xs text-gray-500">{lastRunLine}</p> : null}

      <GcpRecommendConfirmModal
        open={showRecommendConfirm}
        isRunning={isRecommendRunning}
        onClose={closeRecommendConfirm}
        onConfirm={handleConfirmRecommend}
      />
    </div>
  )
}
